import React, { useEffect, useState } from "react";
import { Button, Modal } from "react-bootstrap";
import { faBolt, faComments, faCog, faPoll, faUsers, faVideo } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

const STREAMING_ACTIVITY_HELP_CATEGORIES = [
    {
        id: "audience",
        title: "Interação do público",
        description: "Eventos de participação e sinais agregados de quem acompanha a transmissão.",
        icon: faUsers,
        events: [
            {
                title: "Entrada na sala",
                feed: false,
                timeline: false,
                trigger: "Um espectador entra na sala da transmissão.",
                reading: "Sinal de volume alto usado para contagem e presença. Fica fora do feed principal para não esconder ações operacionais.",
            },
            {
                title: "Feedback",
                feed: false,
                timeline: false,
                trigger: "Curtidas e descurtidas são agregadas.",
                reading: "É um resumo de sentimento de alta frequência. A leitura principal fica nos totais da transmissão.",
            },
            {
                title: "Reações agregadas",
                feed: false,
                timeline: false,
                trigger: "Um lote de reações rápidas é consolidado pelo servidor.",
                reading: "Evento de alto volume usado para métricas de interação. Não aparece no feed para preservar a leitura operacional.",
            },
        ],
    },
    {
        id: "polls",
        title: "Votações",
        description: "Criação, participação e encerramento das votações ao vivo.",
        icon: faPoll,
        events: [
            {
                title: "Votação criada",
                feed: true,
                timeline: true,
                trigger: "O admin publica uma nova votação.",
                reading: "Marca o início de uma interação que pode mudar participação e audiência.",
            },
            {
                title: "Voto registrado",
                feed: false,
                timeline: false,
                trigger: "Um espectador vota em uma opção.",
                reading: "Evento individual de alto volume. A informação útil aparece nos resultados e no engajamento da votação.",
            },
            {
                title: "Votação encerrada",
                feed: true,
                timeline: true,
                trigger: "O admin encerra a votação antes do tempo.",
                reading: "Fecha a janela de participação e congela o engajamento histórico pela amostra de espectadores mais próxima.",
            },
            {
                title: "Votação expirada",
                feed: true,
                timeline: true,
                trigger: "O tempo configurado da votação termina.",
                reading: "Encerramento automático; também serve como marcador de fim da interação.",
            },
            {
                title: "Votação excluída",
                feed: true,
                timeline: true,
                trigger: "O admin remove uma votação do histórico.",
                reading: "Indica que a votação e os votos foram removidos dos relatórios futuros.",
            },
            {
                title: "Votação reutilizada",
                feed: true,
                timeline: true,
                trigger: "O teste automático reaproveita uma votação que já estava ativa.",
                reading: "Marca a preparação técnica do teste sem criar uma segunda votação para a transmissão.",
            },
        ],
    },
    {
        id: "chat",
        title: "Chat e moderação",
        description: "Mudanças de configuração e ações de segurança da conversa.",
        icon: faComments,
        events: [
            {
                title: "Modo do chat",
                feed: true,
                timeline: true,
                trigger: "O admin ativa/desativa chat, modo apenas emojis ou modo lento.",
                reading: "Mudança de controle ao vivo que pode explicar variações em mensagens e participação.",
            },
            {
                title: "Moderação do chat",
                feed: true,
                timeline: false,
                trigger: "Um moderador avisa, silencia, bane, revoga moderação ou remove mensagem.",
                reading: "Ação operacional de segurança/comunidade; deve aparecer mesmo sem aumentar engajamento.",
            },
            {
                title: "Aviso enviado",
                feed: true,
                timeline: true,
                trigger: "O admin envia um aviso global sobre a transmissão.",
                reading: "Mensagem de operação exibida para a audiência e útil como marcador da linha do tempo.",
            },
        ],
    },
    {
        id: "lifecycle",
        title: "Ciclo da transmissão",
        description: "Marcos de operação e alterações nas informações da transmissão.",
        icon: faVideo,
        events: [
            {
                title: "Transmissão iniciada",
                feed: true,
                timeline: false,
                trigger: "O admin inicia a transmissão.",
                reading: "Início operacional da transmissão.",
            },
            {
                title: "Status da transmissão atualizado",
                feed: true,
                timeline: false,
                trigger: "O status da transmissão muda sem ser o início direto.",
                reading: "Ajuda a entender transições de espera, ao vivo e estados administrativos.",
            },
            {
                title: "Informações atualizadas",
                feed: true,
                timeline: false,
                trigger: "Título, descrição, agenda ou dados principais da transmissão são editados.",
                reading: "Alteração de contexto que pode explicar mudança de comportamento dos espectadores.",
            },
            {
                title: "Transmissão encerrada",
                feed: true,
                timeline: false,
                trigger: "O admin encerra a transmissão.",
                reading: "Fim operacional da transmissão; quedas de audiência depois disso são esperadas.",
            },
        ],
    },
    {
        id: "manual",
        title: "Atividades manuais",
        description: "Momentos adicionados pelo admin para contextualizar a operação e a audiência.",
        icon: faBolt,
        events: [
            "Abertura",
            "Palestra",
            "Painel",
            "Votação",
            "Quiz",
            "Perguntas e respostas",
            "Intervalo",
            "Ajuste técnico",
            "Encerramento",
            "Outro momento",
        ].map((title) => ({
            title,
            feed: true,
            timeline: true,
            trigger: "O admin cria uma atividade manual para contextualizar a transmissão.",
            reading: "Marca um momento editorial/operacional que ajuda a explicar variações de audiência, reprodução e engajamento.",
        })),
    },
    {
        id: "technical",
        title: "Teste técnico",
        description: "Registros criados durante a preparação automática de uma transmissão de teste.",
        icon: faCog,
        events: [
            {
                title: "Teste da transmissão preparado",
                feed: true,
                timeline: false,
                trigger: "O teste automático prepara a sala, o chat e os dados de verificação.",
                reading: "Registra o início da preparação técnica para que o resultado do teste possa ser interpretado corretamente.",
            },
        ],
    },
];

const activityVisibilityLabel = (visible, visibleLabel, hiddenLabel) => visible ? visibleLabel : hiddenLabel;

export const StreamingActivityHelpModal = ({ show, onHide }) => {
    const [selectedCategoryId, setSelectedCategoryId] = useState(STREAMING_ACTIVITY_HELP_CATEGORIES[0].id);
    const selectedCategory = STREAMING_ACTIVITY_HELP_CATEGORIES.find(({ id }) => id === selectedCategoryId)
        || STREAMING_ACTIVITY_HELP_CATEGORIES[0];

    useEffect(() => {
        if (show) setSelectedCategoryId(STREAMING_ACTIVITY_HELP_CATEGORIES[0].id);
    }, [show]);

    return (
        <Modal
            show={show}
            onHide={onHide}
            size="lg"
            centered
            scrollable
            className="streaming-activity-help-modal streaming-help-modal"
            backdropClassName="streaming-activity-help-backdrop"
        >
            <Modal.Header closeButton>
                <Modal.Title>Logs das atividades da transmissão</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <p className="streaming-activity-help-intro">
                    Consulte os eventos que podem ser registrados durante a transmissão. O feed principal prioriza ações operacionais; eventos de alto volume continuam resumidos nas métricas.
                </p>
                <div className="streaming-help-sections">
                    <nav className="streaming-help-categories" aria-label="Categorias dos logs">
                        <span className="streaming-help-categories-label">Categorias</span>
                        {STREAMING_ACTIVITY_HELP_CATEGORIES.map((category) => {
                            const isSelected = category.id === selectedCategory.id;
                            return (
                                <button
                                    key={category.id}
                                    type="button"
                                    className={`streaming-help-category${isSelected ? " active" : ""}`}
                                    aria-current={isSelected ? "true" : undefined}
                                    aria-label={`${category.title} ${category.events.length} ${category.events.length === 1 ? "evento" : "eventos"}`}
                                    onClick={() => setSelectedCategoryId(category.id)}
                                >
                                    <span className="streaming-help-category-icon" aria-hidden="true">
                                        <FontAwesomeIcon icon={category.icon} />
                                    </span>
                                    <span>
                                        <strong>{category.title}</strong>
                                        <small>{category.events.length} {category.events.length === 1 ? "evento" : "eventos"}</small>
                                    </span>
                                </button>
                            );
                        })}
                    </nav>
                    <section className="streaming-help-section" aria-labelledby="streaming-help-section-title">
                        <header className="streaming-help-section-header">
                            <span className="streaming-help-section-icon" aria-hidden="true">
                                <FontAwesomeIcon icon={selectedCategory.icon} />
                            </span>
                            <div>
                                <h6 id="streaming-help-section-title">{selectedCategory.title}</h6>
                                <p>{selectedCategory.description}</p>
                            </div>
                        </header>
                        <div className="streaming-activity-help-list">
                            {selectedCategory.events.map((event) => (
                                <article className="streaming-activity-help-item" key={event.title}>
                                    <header>
                                        <div><strong>{event.title}</strong></div>
                                        <div className="streaming-activity-help-badges">
                                            <span className={`streaming-activity-help-badge ${event.feed ? "visible" : "hidden"}`}>
                                                {activityVisibilityLabel(event.feed, "Aparece no feed", "Oculto no feed")}
                                            </span>
                                            <span className={`streaming-activity-help-badge ${event.timeline ? "visible" : "hidden"}`}>
                                                {activityVisibilityLabel(event.timeline, "Aparece na timeline", "Fora da timeline")}
                                            </span>
                                        </div>
                                    </header>
                                    <p><b>Disparo:</b> {event.trigger}</p>
                                    <p><b>Leitura:</b> {event.reading}</p>
                                </article>
                            ))}
                        </div>
                    </section>
                </div>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="primary" onClick={onHide}>Entendi</Button>
            </Modal.Footer>
        </Modal>
    );
};

export default StreamingActivityHelpModal;
